import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronRight, CreditCard, Check, Loader2, ShoppingBag, 
  ArrowLeft, Lock, MapPin, Truck, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shipping constants (should match backend)
const SHIPPING_FEE = 3.50;
const FREE_SHIPPING_THRESHOLD = 60.0;

// Helper to calculate shipping fee (mirrors backend logic)
const calculateShippingFee = (subtotal, hasPressOnNails) => {
  // No shipping for gift cards only
  if (!hasPressOnNails) return 0;
  // Free shipping above threshold
  if (subtotal >= FREE_SHIPPING_THRESHOLD) return 0;
  return SHIPPING_FEE;
};

export default function CheckoutPage() {
  const { items, getTotal } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [step, setStep] = useState(1); // 1: Recap, 2: Shipping, 3: Payment
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess] = useState(false);
  const [orderId] = useState(null);
  
  const [shippingInfo, setShippingInfo] = useState({
    street: '',
    complement: '',
    city: '',
    postalCode: '',
    country: 'France',
    phone: '',
    notes: ''
  });
  
  // Check if cart contains physical products
  const hasPressOnNails = items.some(item => item.type !== 'gift_card');
  
  // Calculate shipping
  const subtotal = getTotal();
  const deliveryFee = calculateShippingFee(subtotal, hasPressOnNails);
  const totalWithShipping = subtotal + deliveryFee;
  const isFreeShipping = deliveryFee === 0;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('canceled') === '1') {
      toast.info('Paiement annulé, vous pouvez réessayer.');
    }
  }, [location.search]);

  // Redirect if not authenticated or cart empty
  if (!isAuthenticated) {
    navigate('/login', { state: { from: { pathname: '/checkout' } } });
    return null;
  }
  
  if (items.length === 0 && !paymentSuccess) {
    navigate('/panier');
    return null;
  }

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    if (!shippingInfo.street || !shippingInfo.city || !shippingInfo.postalCode) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setStep(3);
  };

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      // Prepare items for checkout - handle both Press On Nails and Gift Cards
      const checkoutItems = items.map(item => {
        if (item.type === 'gift_card') {
          return {
            type: 'gift_card',
            gift_type: item.gift_type,
            amount: item.amount,
            totalPrice: item.totalPrice,
            prestation_id: item.prestation_id || null,
            prestation_name: item.prestation_name || null,
            receiver_name: item.receiver_name || null,
            receiver_email: item.receiver_email || null,
            sender_name: item.sender_name || null,
            personal_message: item.personal_message || null
          };
        }
        if (item.type === 'formation_product') {
          return {
            type: 'formation_product',
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: item.quantity || 1,
            totalPrice: item.totalPrice
          };
        }
        // Press On Nails item
        return {
          type: 'press_on_nails',
          shape: item.shape,
          length: item.length,
          color: item.color,
          nailArt: item.nailArt || null,
          nailArtDescription: item.nailArtDescription || null,
          totalPrice: item.totalPrice,
          shapeLabel: item.shapeLabel || null,
          lengthLabel: item.lengthLabel || null,
          colorLabel: item.colorLabel || null,
          nailArtLabel: item.nailArtLabel || null,
          photos: (item.photos || [])
            .map(p => ({
              url: p.url || '',
              type: p.type || null,
              public_id: p.public_id || null,
              uploaded_at: p.uploaded_at || null
            }))
            .filter(p => p.url && !p.url.startsWith('blob:'))
        };
      });

      const hasMissingReferencePhotos = checkoutItems.some(item => {
        if (item.type !== 'press_on_nails') return false;
        const requiredCount = item.nailArt && item.nailArt !== 'none' ? 3 : 2;
        return (item.photos || []).length < requiredCount;
      });
      if (hasMissingReferencePhotos) {
        toast.error("Certaines photos de référence ne sont pas valides. Merci de les recharger avant paiement.");
        setIsProcessing(false);
        return;
      }

      const response = await axios.post(`${API_URL}/payments/stripe/create-checkout-session`, {
        items: checkoutItems,
        // Structured shipping address fields
        shipping_street: shippingInfo.street,
        shipping_postal_code: shippingInfo.postalCode,
        shipping_city: shippingInfo.city,
        shipping_country: shippingInfo.country,
        shipping_complement: shippingInfo.complement || null,
        phone: shippingInfo.phone || null,
        notes: shippingInfo.notes || null
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
        return;
      }
      toast.error('Impossible de rediriger vers Stripe.');
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Success view
  if (paymentSuccess) {
    return (
      <PageTransition>
        <section className="min-h-screen flex items-center justify-center px-6 py-20 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
          <FadeIn>
            <div className="max-w-[500px] mx-auto text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-20 h-20 mx-auto mb-8 rounded-full bg-green-100 flex items-center justify-center"
              >
                <Check size={40} className="text-green-600" />
              </motion.div>
              
              <h1 className="font-['Cormorant_Garamond'] text-4xl mb-4 text-[var(--charcoal)]">
                Commande confirmée !
              </h1>
              
              <p className="text-[var(--charcoal)]/60 mb-2">
                Merci pour votre commande.
              </p>
              <p className="text-sm text-[var(--charcoal)]/50 mb-8">
                N° de commande : <span className="font-mono text-[var(--gold)]">{orderId?.slice(0, 8).toUpperCase()}</span>
              </p>
              
              <div className="bg-white p-6 border border-[var(--champagne)] mb-8 text-left">
                <h3 className="font-medium text-[var(--charcoal)] mb-3 flex items-center gap-2">
                  <MapPin size={16} className="text-[var(--gold)]" />
                  Adresse de livraison
                </h3>
                <p className="text-sm text-[var(--charcoal)]/70">
                  {shippingInfo.street}<br />
                  {shippingInfo.complement && <>{shippingInfo.complement}<br /></>}
                  {shippingInfo.postalCode} {shippingInfo.city}<br />
                  {shippingInfo.country}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={`/mon-compte/commandes/${orderId}`}
                  className="inline-block px-8 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors"
                >
                  Voir ma commande
                </Link>
                <Link
                  to="/"
                  className="inline-block px-8 py-4 border border-[var(--charcoal)] text-[var(--charcoal)] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--charcoal)] hover:text-white transition-colors"
                >
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </FadeIn>
        </section>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-[var(--charcoal)]"
          >
            Finaliser ma commande
          </motion.h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/panier" className="hover:text-[var(--gold)]">Panier</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Checkout</span>
          </nav>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-white border-b border-[var(--champagne)]">
        <div className="max-w-[800px] mx-auto px-6 py-6">
          <div className="flex items-center justify-center gap-4">
            {[
              { num: 1, label: 'Récapitulatif' },
              { num: 2, label: 'Livraison' },
              { num: 3, label: 'Paiement' }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[var(--gold)]' : 'text-[var(--charcoal)]/30'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step > s.num ? 'bg-green-500 text-white' : 
                    step === s.num ? 'bg-[var(--gold)] text-white' : 
                    'bg-[var(--champagne)] text-[var(--charcoal)]/50'
                  }`}>
                    {step > s.num ? <Check size={16} /> : s.num}
                  </div>
                  <span className="hidden sm:inline text-sm">{s.label}</span>
                </div>
                {i < 2 && <div className="w-12 h-[1px] bg-[var(--champagne)] mx-2" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[800px] mx-auto">
          
          {/* Step 1: Recap */}
          {step === 1 && (
            <FadeIn>
              <div className="bg-white p-6 md:p-8 border border-[var(--champagne)]">
                <h2 className="font-['Cormorant_Garamond'] text-2xl mb-6 text-[var(--charcoal)]">
                  Récapitulatif de votre commande
                </h2>
                
                <div className="space-y-4 mb-6">
                  {items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start p-4 bg-[var(--champagne)]/20">
                      <div>
                        <p className="font-medium text-[var(--charcoal)]">
                          {item.type === 'gift_card' && 'Carte cadeau'}
                          {item.type === 'formation_product' && (item.product_name || 'Produit formation')}
                          {(!item.type || item.type === 'press_on_nails') && 'Press On Nails'}
                        </p>
                        {(!item.type || item.type === 'press_on_nails') && (
                          <p className="text-sm text-[var(--charcoal)]/60">
                            {item.shapeLabel || item.shape} • {item.lengthLabel || item.length}
                          </p>
                        )}
                        {item.type === 'formation_product' && (
                          <p className="text-sm text-[var(--charcoal)]/60">Produit physique</p>
                        )}
                      </div>
                      <p className="text-[var(--gold)] font-medium">{item.totalPrice.toFixed(2)}€</p>
                    </div>
                  ))}
                </div>
                
                {/* Shipping info banner */}
                <div className={`flex items-center gap-3 p-4 mb-6 ${isFreeShipping ? 'bg-green-50 border border-green-200' : 'bg-[var(--champagne)]/30'}`}>
                  {isFreeShipping ? (
                    <>
                      <Gift size={20} className="text-green-600" />
                      <div>
                        <p className="text-green-700 font-medium text-sm">Livraison offerte !</p>
                        <p className="text-green-600 text-xs">Votre commande dépasse {FREE_SHIPPING_THRESHOLD}€</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Truck size={20} className="text-[var(--charcoal)]/60" />
                      <div>
                        <p className="text-[var(--charcoal)]/70 text-sm">Frais de livraison : {SHIPPING_FEE.toFixed(2)}€</p>
                        <p className="text-[var(--charcoal)]/50 text-xs">
                          Plus que {(FREE_SHIPPING_THRESHOLD - subtotal).toFixed(2)}€ pour la livraison gratuite
                        </p>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="border-t border-[var(--champagne)] pt-4 mb-6 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[var(--charcoal)]/60">Sous-total</span>
                    <span>{subtotal.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[var(--charcoal)]/60">Livraison</span>
                    <span className={isFreeShipping ? 'text-green-600' : ''}>
                      {isFreeShipping ? 'Offerte' : `${deliveryFee.toFixed(2)}€`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-[var(--champagne)]">
                    <span className="text-lg text-[var(--charcoal)]">Total</span>
                    <span className="font-['Cormorant_Garamond'] text-3xl text-[var(--gold)]">{totalWithShipping.toFixed(2)}€</span>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <Link
                    to="/panier"
                    className="flex items-center gap-2 px-6 py-3 border border-[var(--charcoal)] text-[var(--charcoal)] text-sm hover:bg-[var(--charcoal)] hover:text-white transition-colors"
                  >
                    <ArrowLeft size={16} />
                    Modifier
                  </Link>
                  <Button
                    onClick={() => setStep(2)}
                    className="flex-1 py-3 bg-[var(--gold)] text-white text-sm uppercase tracking-[0.15em] hover:bg-[var(--dark-gold)] rounded-none"
                  >
                    Continuer
                  </Button>
                </div>
              </div>
            </FadeIn>
          )}
          
          {/* Step 2: Shipping */}
          {step === 2 && (
            <FadeIn>
              <form onSubmit={handleShippingSubmit} className="bg-white p-6 md:p-8 border border-[var(--champagne)]">
                <h2 className="font-['Cormorant_Garamond'] text-2xl mb-6 text-[var(--charcoal)] flex items-center gap-2">
                  <MapPin size={24} className="text-[var(--gold)]" />
                  Adresse de livraison
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <Label>Rue <span className="text-red-500">*</span></Label>
                    <Input
                      value={shippingInfo.street}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, street: e.target.value }))}
                      placeholder="123 Rue de Paris"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Complément d'adresse (optionnel)</Label>
                    <Input
                      value={shippingInfo.complement}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, complement: e.target.value }))}
                      placeholder="Appartement, bâtiment, étage..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Code postal <span className="text-red-500">*</span></Label>
                      <Input
                        value={shippingInfo.postalCode}
                        onChange={(e) => setShippingInfo(prev => ({ ...prev, postalCode: e.target.value }))}
                        placeholder="75001"
                        required
                      />
                    </div>
                    <div>
                      <Label>Ville <span className="text-red-500">*</span></Label>
                      <Input
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Paris"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Pays</Label>
                    <Input
                      value={shippingInfo.country}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, country: e.target.value }))}
                      placeholder="France"
                    />
                  </div>
                  
                  <div>
                    <Label>Téléphone (optionnel)</Label>
                    <Input
                      value={shippingInfo.phone}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                      type="tel"
                    />
                    <p className="text-xs text-[var(--charcoal)]/50 mt-1">Pour vous contacter en cas de besoin</p>
                  </div>
                  
                  <div>
                    <Label>Instructions de livraison (optionnel)</Label>
                    <Textarea
                      value={shippingInfo.notes}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Code d'accès, interphone, horaires préférés..."
                      className="min-h-[80px]"
                    />
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="px-6 py-3 rounded-none"
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Retour
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 py-3 bg-[var(--gold)] text-white text-sm uppercase tracking-[0.15em] hover:bg-[var(--dark-gold)] rounded-none"
                  >
                    Continuer vers le paiement
                  </Button>
                </div>
              </form>
            </FadeIn>
          )}
          
          {/* Step 3: Payment */}
          {step === 3 && (
            <FadeIn>
              <div className="bg-white p-6 md:p-8 border border-[var(--champagne)]">
                <h2 className="font-['Cormorant_Garamond'] text-2xl mb-6 text-[var(--charcoal)] flex items-center gap-2">
                  <CreditCard size={24} className="text-[var(--gold)]" />
                  Paiement
                </h2>
                
                {/* Order Summary */}
                <div className="bg-[var(--champagne)]/20 p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[var(--charcoal)]/60">Sous-total ({items.length} article{items.length > 1 ? 's' : ''})</span>
                    <span>{subtotal.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[var(--charcoal)]/60 flex items-center gap-1">
                      <Truck size={14} />
                      Livraison
                    </span>
                    <span className={isFreeShipping ? 'text-green-600 font-medium' : ''}>
                      {isFreeShipping ? 'Offerte' : `${deliveryFee.toFixed(2)}€`}
                    </span>
                  </div>
                  <div className="border-t border-[var(--champagne)] pt-2 mt-2 flex justify-between items-center">
                    <span className="font-medium text-[var(--charcoal)]">Total</span>
                    <span className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)]">{totalWithShipping.toFixed(2)}€</span>
                  </div>
                </div>
                
                {/* Shipping address recap */}
                <div className="bg-[var(--champagne)]/10 p-4 mb-6">
                  <h4 className="text-sm font-medium text-[var(--charcoal)] mb-2 flex items-center gap-2">
                    <MapPin size={14} className="text-[var(--gold)]" />
                    Livraison à
                  </h4>
                  <p className="text-sm text-[var(--charcoal)]/70">
                    {shippingInfo.street}
                    {shippingInfo.complement && <>, {shippingInfo.complement}</>}<br />
                    {shippingInfo.postalCode} {shippingInfo.city}, {shippingInfo.country}
                  </p>
                </div>
                
                
                {/* Security Badge */}
                <div className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60 mb-6">
                  <Lock size={16} className="text-green-600" />
                  <span>Paiement 100% sécurisé</span>
                </div>
                
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="px-6 py-3 rounded-none"
                    disabled={isProcessing}
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Retour
                  </Button>
                  <Button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className="flex-1 py-4 bg-[var(--gold)] text-white text-sm uppercase tracking-[0.15em] hover:bg-[var(--dark-gold)] rounded-none disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 size={18} className="animate-spin mr-2" />
                        Traitement en cours...
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} className="mr-2" />
                        Payer {totalWithShipping.toFixed(2)}€
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </FadeIn>
          )}
          
        </div>
      </section>
    </PageTransition>
  );
}
